import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Switch,
  Tooltip,
  Popconfirm,
  Typography,
  Alert,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import { integrationService } from '@services/admin/integrationService';

const { Text } = Typography;

export default function AdminIntegrationsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState([]);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const data = await integrationService.getList();
      if (data.success) {
        setIntegrations(data.data || []);
      }
    } catch (error) {
      message.error(t('shared.integrations.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const data = await integrationService.toggleActive(id);
      if (data.success) {
        message.success(t('shared.integrations.messages.toggle_success'));
        fetchIntegrations();
      }
    } catch (error) {
      message.error(t('shared.integrations.error.update_failed'));
    }
  };

  const handleDelete = async (id) => {
    try {
      const data = await integrationService.delete(id);
      if (data.success) {
        message.success(t('shared.integrations.messages.delete_success'));
        fetchIntegrations();
      } else {
        message.error(data.message || t('shared.integrations.error.delete_failed'));
      }
    } catch (error) {
      message.error(t('shared.integrations.error.delete_failed'));
    }
  };

  const columns = [
    {
      title: t('shared.integrations.fields.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => navigate(`/integrations/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: t('shared.integrations.fields.client_id'),
      dataIndex: 'client_id',
      key: 'client_id',
      render: (text) => (
        <Text code copyable style={{ fontSize: 12 }}>
          {text}
        </Text>
      ),
    },
    {
      title: t('shared.integrations.fields.scopes'),
      dataIndex: 'scopes',
      key: 'scopes',
      width: 220,
      render: (scopes) => (
        <Space wrap size={[4, 4]}>
          {(scopes || []).slice(0, 3).map((scope) => (
            <Tag key={scope} size="small">
              {t(`shared.integrations.scopes.${scope.replace(':', '_')}`, scope)}
            </Tag>
          ))}
          {scopes && scopes.length > 3 && (
            <Tag>+{scopes.length - 3}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('shared.integrations.detail.webhook'),
      dataIndex: 'webhook_url',
      key: 'webhook_url',
      ellipsis: true,
      width: 200,
      render: (url, record) =>
        url ? (
          <Tooltip title={url}>
            <Badge status={record.webhook_active ? 'success' : 'default'} />
            <Text code style={{ fontSize: 11 }}>{url}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: t('shared.integrations.fields.active'),
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={() => handleToggleActive(record.id)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      ),
    },
    {
      title: t('shared.integrations.fields.last_used_at'),
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 170,
      render: (text) => (text ? new Date(text).toLocaleString() : '-'),
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title={t('common.button.view')}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/integrations/${record.id}`)}
            />
          </Tooltip>
          <Popconfirm
            title={t('shared.integrations.confirm.delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
          >
            <Tooltip title={t('common.button.delete')}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <ListPageHeader basePath="/admin" items={['developer_tools', 'developer_tools.integrations']} />
      <Card>
        <Alert
          message={t('shared.integrations.description')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/integrations/new')}
          >
            {t('common.button.create')}
          </Button>
        </div>
        <Table
          dataSource={integrations}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: t('shared.integrations.empty') }}
        />
      </Card>
    </div>
  );
}
